import React, { useEffect, useState } from "react";
import axios from "axios";
import { Play, X, Share2, Download, Loader2, Clock } from "lucide-react";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const GOLD = "#D4AF37";
export const RED = "#C41E24";
export const SHARE_TEXT = "Check out my latest session with Coach Barker — Next O Technical Academy 🔥 hub.nextosoccer.com";
export const fmtDay = (d) => { const t = new Date(`${d}T12:00:00`); return isNaN(t) ? d : t.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" }); };
export const clipSrc = (clip, token) => `${API}${clip.video_url}?auth=${token}`;

export const ClipThumb = ({ clip, token, onClick, testid, className = "", children }) => (
  <button type="button" data-testid={testid} onClick={onClick} className={`group relative overflow-hidden rounded-lg border border-white/10 bg-black ${className}`}>
    <video src={`${clipSrc(clip, token)}#t=0.1`} preload="metadata" muted playsInline className="w-full h-full object-cover opacity-85 group-hover:opacity-100 transition-opacity duration-300" />
    <span className="absolute inset-0 flex items-center justify-center"><span className="w-10 h-10 rounded-full bg-black/55 backdrop-blur-sm border border-white/25 flex items-center justify-center group-hover:scale-110 transition-transform duration-300"><Play size={15} className="text-white ml-0.5" /></span></span>
    {children}
  </button>
);

export const CoachNote = ({ note, testid }) => note ? (
  <div data-testid={testid} className="rounded-lg border px-4 py-3" style={{ borderColor: "rgba(196,30,36,0.4)", background: "rgba(196,30,36,0.10)" }}>
    <div className="font-mono text-[9px] font-bold uppercase tracking-[0.3em] text-[#ff8a8e] mb-1">Coach Barker's note</div>
    <p className="italic text-[15px] leading-relaxed text-white/90">“{note}”</p>
  </div>
) : null;

export const ClipLightbox = ({ clip, token, onClose }) => {
  useEffect(() => { const k = (e) => e.key === "Escape" && onClose(); window.addEventListener("keydown", k); return () => window.removeEventListener("keydown", k); }, [onClose]);
  return (
    <div data-testid="recap-lightbox" role="dialog" aria-modal="true" className="fixed inset-0 z-[95] bg-black/95 flex flex-col" onClick={onClose}>
      <div className="flex items-center justify-between gap-3 px-4 safe-top" onClick={(e) => e.stopPropagation()}>
        <div className="min-w-0 py-3"><div className="font-headings uppercase tracking-wide text-lg truncate">{clip.first_name}</div>{clip.move_label && <div className="font-mono text-[10px] uppercase tracking-[0.25em]" style={{ color: GOLD }}>{clip.move_label}</div>}</div>
        <button data-testid="recap-lightbox-close" onClick={onClose} aria-label="Close" className="w-11 h-11 flex items-center justify-center rounded-full bg-white/10 text-white"><X size={18} /></button>
      </div>
      <div className="flex-1 flex items-center justify-center p-3 min-h-0" onClick={(e) => e.stopPropagation()}>
        <video data-testid="recap-lightbox-video" src={clipSrc(clip, token)} controls autoPlay playsInline className="h-full max-h-full aspect-[9/16] max-w-full object-contain rounded-lg bg-black" />
      </div>
      {clip.note && <div className="px-4 pb-6 max-w-lg w-full mx-auto" onClick={(e) => e.stopPropagation()}><CoachNote note={clip.note} testid="recap-lightbox-note" /></div>}
    </div>
  );
};

const fetchShareFile = async (clip, token) => {
  const { data } = await axios.get(`${API}/highlights/share-file/${clip.id}`, { headers: { Authorization: `Bearer ${token}` }, responseType: "blob" });
  const type = data.type || "video/mp4";
  return new File([data], `next-o-highlight-${clip.id.slice(0, 8)}.${type.includes("quicktime") ? "mov" : "mp4"}`, { type });
};

const btn = "inline-flex items-center justify-center gap-2 min-h-[48px] px-5 rounded-sm text-xs font-bold uppercase tracking-wider transition-colors disabled:opacity-60";

export const ShareClipButton = ({ clip, token }) => {
  const canShare = typeof navigator !== "undefined" && !!navigator.share && !!navigator.canShare;
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!clip.share_ready || !canShare) return undefined;
    let live = true;
    setBusy(true);
    fetchShareFile(clip, token).then((f) => { if (live && navigator.canShare({ files: [f] })) setFile(f); }).catch(() => {}).finally(() => live && setBusy(false));
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clip.id, clip.share_ready, token, canShare]);
  if (!clip.share_ready) return <span data-testid={`recap-share-soon-${clip.id}`} className={`${btn} border border-white/15 text-white/55`}><Clock size={14} /> Share — coming soon</span>;
  const download = async () => {
    setBusy(true);
    try {
      const f = file || await fetchShareFile(clip, token);
      const url = URL.createObjectURL(f);
      const a = Object.assign(document.createElement("a"), { href: url, download: f.name });
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { /* keep button usable */ }
    setBusy(false);
  };
  const share = async () => {
    try { await navigator.share({ files: [file], text: SHARE_TEXT }); } catch (e) { if (e?.name !== "AbortError") download(); }
  };
  return (
    <div className="flex flex-wrap gap-2">
      {file && <button data-testid={`recap-share-${clip.id}`} onClick={share} className={`${btn} bg-[#C41E24] hover:bg-[#A0161D] text-white`}><Share2 size={14} /> Share</button>}
      <button data-testid={`recap-download-${clip.id}`} onClick={download} disabled={busy} className={`${btn} ${file ? "border border-white/20 text-white/80 hover:text-white" : "bg-[#C41E24] hover:bg-[#A0161D] text-white"}`}>
        {busy ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />} {busy && canShare && !file ? "Preparing…" : "Download"}
      </button>
    </div>
  );
};
